/*
 * Zaun3D – kleiner Canvas-3D-Renderer für den Zaunkonfigurator 3D.
 * Bewusst ohne Abhängigkeiten (kein three.js): Quader und parametrische
 * Holzprofile werden direkt per Projektion, Backface-Culling und
 * Painter-Algorithmus gezeichnet. Läuft lokal und als Shopify-Asset.
 *
 * API:
 *   var ansicht = Zaun3D.erstelle(canvas);   // hängt Orbit-/Zoom-Steuerung an
 *   ansicht.setzeSzene(szene[, ansichtAnpassen]);
 *   ansicht.render();
 *
 *   Zaun3D.szeneAusPlan(plan) -> szene       // baut Boxen aus dem Zaunverlauf
 *     plan: { punkte: [{x,y,z}],             // Verlauf in cm, z = Geländehöhe
 *             hoeheCm, latte: {breite_cm, anzahl_pro_m},
 *             felderProSegment: [[cm,…],…],  // Feldlängen je Abschnitt
 *             material, pfostenFarbe?, mitPfosten?, schraeg?,
 *             pfostensystem?, befestigungId?,
 *             halterung?, skizze? }          // skizze: Kopfform der Latten
 *                                            // pfostensystem: 'ipf' | 'teisen' | 'halter'
 *                                            // halterung: 'ipf' | 'ipf-duebel' | 'hsteg' | 'duebelhalter'
 *
 * Koordinaten: x/y = Grundriss (cm), z = Höhe (cm).
 * Gefälle-Darstellung: Felder waagerecht, dem Gelände folgend gestuft.
 * Mit schraeg: true (Bausatz-Schrägmontage) folgen die Riegel dem Hang und
 * die Latten stehen senkrecht ("im Wasser") – ohne Stufen.
 */
(function (root, factory) {
  if (typeof module !== 'undefined' && module.exports) { module.exports = factory(); }
  else { root.Zaun3D = factory(); }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ---------- Farb-Helfer ---------- */

  function rgb(hex) {
    var h = hex.replace('#', '');
    return [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16)];
  }

  function schattiere(farbe, faktor) {
    var c = rgb(farbe);
    return 'rgb(' + Math.round(c[0] * faktor) + ',' + Math.round(c[1] * faktor) + ',' + Math.round(c[2] * faktor) + ')';
  }

  /* ---------- Geometrie: Quader -> Flächen ---------- */

  /*
   * box: { x, y, z, dx, dy, dz, yaw, pitch?, farbe }
   *   (x, y) = Mittelpunkt im Grundriss, z = Unterkante,
   *   dx = Länge entlang der (um yaw gedrehten) Achse, dy = Tiefe, dz = Höhe.
   *   pitch kippt die Box um ihre Mitte entlang der Längsachse (für Riegel,
   *   die bei der Schrägmontage dem Hang folgen).
   */
  function boxFlaechen(box, ziel) {
    var c = Math.cos(box.yaw || 0), s = Math.sin(box.yaw || 0);
    var cp = Math.cos(box.pitch || 0), sp = Math.sin(box.pitch || 0);
    var hx = box.dx / 2, hy = box.dy / 2, hz = box.dz / 2;

    function punkt(lx, ly, lz) {
      var lzc = lz - hz;                       // Kippung um die Box-Mitte
      var rx = lx * cp - lzc * sp;
      var rz = lx * sp + lzc * cp + hz;
      return [box.x + rx * c - ly * s, box.y + rx * s + ly * c, box.z + rz];
    }
    // 8 Ecken: unten 0-3, oben 4-7 (gegen den Uhrzeigersinn von oben gesehen)
    var e = [
      punkt(-hx, -hy, 0), punkt(hx, -hy, 0), punkt(hx, hy, 0), punkt(-hx, hy, 0),
      punkt(-hx, -hy, box.dz), punkt(hx, -hy, box.dz), punkt(hx, hy, box.dz), punkt(-hx, hy, box.dz)
    ];
    // Flächen mit lokaler Außennormale; gedreht wird wie die Punkte (Pitch, dann Yaw)
    var flaechen = [
      { idx: [0, 1, 5, 4], n: [0, -1, 0] },   // vorn
      { idx: [2, 3, 7, 6], n: [0, 1, 0] },    // hinten
      { idx: [1, 2, 6, 5], n: [1, 0, 0] },    // rechts
      { idx: [3, 0, 4, 7], n: [-1, 0, 0] },   // links
      { idx: [4, 5, 6, 7], n: [0, 0, 1] },    // oben
      { idx: [3, 2, 1, 0], n: [0, 0, -1] }    // unten
    ];
    for (var i = 0; i < flaechen.length; i++) {
      var f = flaechen[i];
      var nx = f.n[0] * cp - f.n[2] * sp;
      var nz = f.n[0] * sp + f.n[2] * cp;
      ziel.push({
        punkte: [e[f.idx[0]], e[f.idx[1]], e[f.idx[2]], e[f.idx[3]]],
        normale: [nx * c - f.n[1] * s, nx * s + f.n[1] * c, nz],
        farbe: box.farbe,
        prio: box.prio || 0 // Anbauteile auf großen Flächen gewinnen den Tiefen-Vergleich
      });
    }
  }

  /* Konvexe Profilnetze in beleuchtete Renderflaechen umwandeln. */
  function formFlaechen(form, ziel) {
    var objektMitte = [0, 0, 0];
    form.punkte.forEach(function (p) {
      objektMitte[0] += p[0]; objektMitte[1] += p[1]; objektMitte[2] += p[2];
    });
    objektMitte = objektMitte.map(function (v) { return v / form.punkte.length; });

    form.flaechen.forEach(function (idx) {
      var punkte = idx.map(function (i) { return form.punkte[i]; });
      var u = [punkte[1][0] - punkte[0][0], punkte[1][1] - punkte[0][1], punkte[1][2] - punkte[0][2]];
      var v = [punkte[2][0] - punkte[0][0], punkte[2][1] - punkte[0][1], punkte[2][2] - punkte[0][2]];
      var n = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
      var laenge = Math.hypot(n[0], n[1], n[2]);
      if (laenge < 0.00001) return;
      n = [n[0] / laenge, n[1] / laenge, n[2] / laenge];

      var flaechenMitte = [0, 0, 0];
      punkte.forEach(function (p) {
        flaechenMitte[0] += p[0]; flaechenMitte[1] += p[1]; flaechenMitte[2] += p[2];
      });
      flaechenMitte = flaechenMitte.map(function (x) { return x / punkte.length; });
      var nachAussen = (flaechenMitte[0] - objektMitte[0]) * n[0] +
        (flaechenMitte[1] - objektMitte[1]) * n[1] + (flaechenMitte[2] - objektMitte[2]) * n[2];
      if (nachAussen < 0) { punkte.reverse(); n = [-n[0], -n[1], -n[2]]; }
      ziel.push({ punkte: punkte, normale: n, farbe: form.farbe, prio: form.prio || 0 });
    });
  }

  /* ---------- Ansicht (Kamera + Steuerung + Rendern) ---------- */

  function erstelle(canvas) {
    var ctx = canvas.getContext('2d');
    var szene = { boxen: [], formen: [], boeden: [] };
    var kamera = { theta: 0.7, phi: 0.42, dist: 1500, ziel: [0, 0, 60] };
    // Licht kommt leicht von vorne/oben. Dadurch bleiben Halbrund- und
    // Rundprofile auch in der Standardansicht als Woelbung erkennbar.
    var licht = normiere([0.45, -0.35, 0.85]);

    function normiere(v) {
      var l = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]) || 1;
      return [v[0] / l, v[1] / l, v[2] / l];
    }
    function dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
    function sub(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }
    function kreuz(a, b) {
      return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
    }

    function passeGroesseAn() {
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      var w = canvas.clientWidth || 600;
      var h = canvas.clientHeight || 380;
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
    }

    /* Kamera so setzen, dass die ganze Szene ins Bild passt. */
    function ansichtAnpassen() {
      var min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
      function nimm(p) {
        for (var k = 0; k < 3; k++) {
          if (p[k] < min[k]) min[k] = p[k];
          if (p[k] > max[k]) max[k] = p[k];
        }
      }
      szene.boxen.forEach(function (b) {
        var r = Math.max(b.dx, b.dy) / 2;
        nimm([b.x - r, b.y - r, b.z]); nimm([b.x + r, b.y + r, b.z + b.dz]);
      });
      (szene.formen || []).forEach(function (form) { form.punkte.forEach(nimm); });
      szene.boeden.forEach(function (bo) { bo.punkte.forEach(nimm); });
      if (min[0] === Infinity) return;
      kamera.ziel = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
      var diag = Math.sqrt(
        Math.pow(max[0] - min[0], 2) + Math.pow(max[1] - min[1], 2) + Math.pow(max[2] - min[2], 2));
      kamera.dist = Math.max(400, diag * 1.05);
    }

    function render() {
      passeGroesseAn();
      var w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      var eye = [
        kamera.ziel[0] + kamera.dist * Math.cos(kamera.phi) * Math.sin(kamera.theta),
        kamera.ziel[1] - kamera.dist * Math.cos(kamera.phi) * Math.cos(kamera.theta),
        kamera.ziel[2] + kamera.dist * Math.sin(kamera.phi)
      ];
      var vor = normiere(sub(kamera.ziel, eye));
      var rechts = normiere(kreuz(vor, [0, 0, 1]));
      var oben = kreuz(rechts, vor);
      var fokal = h * 1.05; // ~50° Blickwinkel
      var nah = 20;

      function projiziere(p) {
        var d = sub(p, eye);
        var zc = dot(d, vor);
        if (zc < nah) return null;
        return [w / 2 + fokal * dot(d, rechts) / zc, h / 2 - fokal * dot(d, oben) / zc, zc];
      }

      function zeichneFlaechen(flaechen, konturStaerke) {
        var sichtbar = [];
        for (var i = 0; i < flaechen.length; i++) {
          var f = flaechen[i];
          var mitte = [0, 0, 0];
          for (var k = 0; k < f.punkte.length; k++) {
            mitte[0] += f.punkte[k][0]; mitte[1] += f.punkte[k][1]; mitte[2] += f.punkte[k][2];
          }
          mitte = [mitte[0] / f.punkte.length, mitte[1] / f.punkte.length, mitte[2] / f.punkte.length];
          if (f.normale && dot(f.normale, sub(eye, mitte)) <= 0) continue; // Rückseite
          var projizierte = [];
          var ok = true, tiefe = 0;
          for (var j = 0; j < f.punkte.length; j++) {
            var q = projiziere(f.punkte[j]);
            if (!q) { ok = false; break; }
            projizierte.push(q);
            tiefe += q[2];
          }
          if (!ok) continue;
          // prio zieht die Fläche im Painter-Vergleich nach vorn: kleine
          // Anbauteile (Haltetechnik) liegen direkt auf großen Pfostenflächen,
          // deren Durchschnittstiefe sonst oft "näher" wirkt.
          sichtbar.push({ p: projizierte, tiefe: tiefe / projizierte.length - (f.prio || 0) * 30, f: f });
        }
        sichtbar.sort(function (a, b) { return b.tiefe - a.tiefe; });
        for (var m = 0; m < sichtbar.length; m++) {
          var e2 = sichtbar[m];
          var hell = e2.f.normale ? 0.62 + 0.38 * Math.max(0, dot(e2.f.normale, licht)) : 1;
          ctx.beginPath();
          ctx.moveTo(e2.p[0][0], e2.p[0][1]);
          for (var n2 = 1; n2 < e2.p.length; n2++) ctx.lineTo(e2.p[n2][0], e2.p[n2][1]);
          ctx.closePath();
          ctx.fillStyle = e2.f.normale ? schattiere(e2.f.farbe, hell) : e2.f.farbe;
          ctx.fill();
          if (konturStaerke) {
            ctx.strokeStyle = 'rgba(60,45,25,0.18)';
            ctx.lineWidth = konturStaerke;
            ctx.stroke();
          }
        }
      }

      // Böden zuerst (liegen unter allem), dann die Zaun-Quader
      zeichneFlaechen(szene.boeden.map(function (b) {
        return { punkte: b.punkte, normale: null, farbe: b.farbe };
      }), 0);
      var flaechen = [];
      szene.boxen.forEach(function (b) { boxFlaechen(b, flaechen); });
      (szene.formen || []).forEach(function (form) { formFlaechen(form, flaechen); });
      zeichneFlaechen(flaechen, Math.max(0.5, canvas.width / 1600));
    }

    /* ----- Maus-/Touch-Steuerung: ziehen = drehen, Rad/Pinch = zoomen ----- */

    var zeiger = {};   // aktive Pointer
    var letzterAbstand = 0;

    canvas.style.touchAction = 'none';
    canvas.addEventListener('pointerdown', function (e) {
      zeiger[e.pointerId] = [e.clientX, e.clientY];
      canvas.setPointerCapture(e.pointerId);
    });
    canvas.addEventListener('pointermove', function (e) {
      if (!zeiger[e.pointerId]) return;
      var ids = Object.keys(zeiger);
      var dx = e.clientX - zeiger[e.pointerId][0];
      var dy = e.clientY - zeiger[e.pointerId][1];
      zeiger[e.pointerId] = [e.clientX, e.clientY];

      if (ids.length === 2) {
        // Pinch-Zoom
        var a = zeiger[ids[0]], b = zeiger[ids[1]];
        var abstand = Math.hypot(a[0] - b[0], a[1] - b[1]);
        if (letzterAbstand) kamera.dist *= letzterAbstand / abstand;
        letzterAbstand = abstand;
      } else if (e.shiftKey || e.buttons === 4) {
        // Verschieben (Shift+Ziehen oder mittlere Taste)
        var f = kamera.dist / 900;
        kamera.ziel[0] -= (dx * Math.cos(kamera.theta) + dy * Math.sin(kamera.theta) * Math.sin(kamera.phi)) * f;
        kamera.ziel[1] -= (dx * Math.sin(kamera.theta) - dy * Math.cos(kamera.theta) * Math.sin(kamera.phi)) * f;
        kamera.ziel[2] += dy * Math.cos(kamera.phi) * f;
      } else {
        kamera.theta += dx * 0.008;
        kamera.phi = Math.min(1.45, Math.max(0.06, kamera.phi + dy * 0.006));
      }
      render();
    });
    function zeigerLos(e) {
      delete zeiger[e.pointerId];
      letzterAbstand = 0;
    }
    canvas.addEventListener('pointerup', zeigerLos);
    canvas.addEventListener('pointercancel', zeigerLos);
    canvas.addEventListener('wheel', function (e) {
      e.preventDefault();
      kamera.dist = Math.min(20000, Math.max(200, kamera.dist * Math.exp(e.deltaY * 0.001)));
      render();
    }, { passive: false });

    return {
      setzeSzene: function (neu, anpassen) {
        szene = neu;
        if (anpassen !== false) ansichtAnpassen();
        render();
      },
      ansichtAnpassen: function () { ansichtAnpassen(); render(); },
      render: render,
      kamera: kamera
    };
  }

  /* ---------- Szenen-Builder: Zaunverlauf -> Profilgeometrie ---------- */

  var FARBEN = {
    fichte: { latte: '#c99a68', riegel: '#b98a55', pfosten: '#8a5f33' },
    laerche: { latte: '#e5c07d', riegel: '#dab469', pfosten: '#c79a55' },
    pfosten: '#8a5f33',
    pfostenAlt: '#8d8273',
    stahl: '#a6abb1', // feuerverzinkt (silbrig), hebt sich vom braunen Pfosten ab
    stahlDunkel: '#7f8589',
    boden: '#9aa387',
    bodenDunkel: '#8a9377'
  };

  function netzAusRingen(ringe, transformiere, farbe, meta) {
    var punkte = [];
    var flaechen = [];
    var ringGroesse = ringe[0].length;
    ringe.forEach(function (ring) {
      ring.forEach(function (p) { punkte.push(transformiere(p)); });
    });
    flaechen.push(Array.from({ length: ringGroesse }, function (_, i) { return i; }));
    for (var r = 0; r < ringe.length - 1; r++) {
      var a = r * ringGroesse, b = (r + 1) * ringGroesse;
      for (var i = 0; i < ringGroesse; i++) {
        var j = (i + 1) % ringGroesse;
        flaechen.push([a + i, a + j, b + j, b + i]);
      }
    }
    var oben = (ringe.length - 1) * ringGroesse;
    flaechen.push(Array.from({ length: ringGroesse }, function (_, i) { return oben + i; }));
    return { punkte: punkte, flaechen: flaechen, farbe: farbe, meta: meta || {} };
  }

  function lattenQuerschnitt(latte, breite) {
    var form = String(latte.form || '').toLowerCase();
    var profil = form === 'zylinder' ? 'zylinder' :
      (form === 'halbrund' ? 'halbrund' : (form === 'quader' ? 'eckig' : null));
    if (!profil) profil = latte.profil === 'rund' ? 'halbrund' : 'eckig';
    var punkte = [];
    var tiefe;

    if (profil === 'zylinder') {
      tiefe = breite;
      for (var i = 0; i < 12; i++) {
        var winkel = 2 * Math.PI * i / 12;
        punkte.push([Math.cos(winkel) * breite / 2, Math.sin(winkel) * tiefe / 2]);
      }
    } else if (profil === 'halbrund') {
      tiefe = breite / 2;
      for (var j = 0; j <= 10; j++) {
        var bogen = Math.PI - Math.PI * j / 10;
        punkte.push([Math.cos(bogen) * breite / 2, tiefe / 2 - Math.sin(bogen) * tiefe]);
      }
    } else {
      tiefe = breite <= 4.2 ? breite : Math.min(2.8, breite * 0.45);
      punkte = [[-breite / 2, -tiefe / 2], [breite / 2, -tiefe / 2],
        [breite / 2, tiefe / 2], [-breite / 2, tiefe / 2]];
    }
    return { punkte: punkte, tiefe: tiefe, profil: profil };
  }

  function lattenForm(px, py, z, yaw, breite, hoehe, latte, skizze, farbe) {
    var q = lattenQuerschnitt(Object.assign({}, latte, { profil: skizze.profil }), breite);
    var kopf = skizze.kopf || 'flach';
    var ringe = [];
    function ring(zWert, skala, zFunktion) {
      return q.punkte.map(function (p) {
        return [p[0] * skala, p[1] * skala, zFunktion ? zFunktion(p[1]) : zWert];
      });
    }
    ringe.push(ring(0, 1));

    if (kopf === 'spitz') {
      // Halbrundlatten wirken mit einer etwas flacheren Spitze realistischer;
      // volle Rundhoelzer behalten ihren ausgepraegten, kegelfoermigen Kopf.
      var profilFaktor = q.profil === 'halbrund' ? 0.72 : 1;
      var spitzenHoehe = Math.min(hoehe * 0.25,
        breite * (Number(skizze.spitzeFaktor) || 1.8) * profilFaktor);
      ringe.push(ring(hoehe - spitzenHoehe, 1));
      ringe.push(ring(hoehe, 0.025));
    } else if (kopf === 'rund') {
      var radius = Math.min(breite / 2, hoehe * 0.2);
      ringe.push(ring(hoehe - radius, 1));
      for (var k = 1; k <= 5; k++) {
        var t = k / 5;
        ringe.push(ring(hoehe - radius + radius * t, Math.max(0.025, Math.sqrt(1 - t * t))));
      }
    } else if (kopf === 'schraeg') {
      var minY = Math.min.apply(null, q.punkte.map(function (p) { return p[1]; }));
      var maxY = Math.max.apply(null, q.punkte.map(function (p) { return p[1]; }));
      var schnitt = Math.min(hoehe * 0.15, breite * (Number(skizze.schnittFaktor) || 0.5));
      ringe.push(ring(hoehe, 1, function (y) {
        return hoehe - schnitt * (maxY - y) / Math.max(0.01, maxY - minY);
      }));
    } else {
      ringe.push(ring(hoehe, 1));
    }

    var c = Math.cos(yaw), s = Math.sin(yaw);
    return netzAusRingen(ringe, function (p) {
      return [px + p[0] * c - p[1] * s, py + p[0] * s + p[1] * c, z + p[2]];
    }, farbe, { typ: 'latte', profil: q.profil, kopf: kopf, tiefe: q.tiefe, hoehe: hoehe });
  }

  function riegelForm(px, py, zMitte, yaw, pitch, laenge, farbe) {
    var tiefe = 4.5, hoehe = 8;
    var querschnitt = [];
    for (var i = 0; i <= 10; i++) {
      var winkel = -Math.PI / 2 + Math.PI * i / 10;
      querschnitt.push([tiefe / 2 - Math.cos(winkel) * tiefe, Math.sin(winkel) * hoehe / 2]);
    }
    var ringe = [-laenge / 2, laenge / 2].map(function (x) {
      return querschnitt.map(function (p) { return [x, p[0], p[1]]; });
    });
    var c = Math.cos(yaw), s = Math.sin(yaw), cp = Math.cos(pitch || 0), sp = Math.sin(pitch || 0);
    return netzAusRingen(ringe, function (p) {
      var rx = p[0] * cp - p[2] * sp;
      var rz = p[0] * sp + p[2] * cp;
      return [px + rx * c - p[1] * s, py + rx * s + p[1] * c, zMitte + rz];
    }, farbe, { typ: 'riegel', profil: 'halbrund' });
  }

  function szeneAusPlan(plan) {
    var boxen = [];
    var formen = [];
    var boeden = [];
    var punkte = plan.punkte || [];
    if (punkte.length < 2) return { boxen: boxen, formen: formen, boeden: boeden };

    var holz = /rche/i.test(plan.material || '') ? FARBEN.laerche : FARBEN.fichte;
    var pfostenFarbe = plan.pfostenFarbe || holz.pfosten;
    var mitPfosten = plan.mitPfosten !== false;
    var hoehe = plan.hoeheCm || 100;
    var latte = plan.latte || {};
    var lb = Number(latte.breite_cm) || 6.4;
    var proM = Number(latte.anzahl_pro_m) || 9;
    var skizze = plan.skizze || {};
    var lattenProfil = lattenQuerschnitt(Object.assign({}, latte, { profil: skizze.profil }), lb);
    var lattenTiefe = lattenProfil.tiefe;
    var pfostensystem = plan.pfostensystem || (plan.pfostenTyp === 'teisen' ? 'teisen' : 'ipf');
    var istTeisen = pfostensystem === 'teisen';
    var q = istTeisen ? 4 : 9.2; // Pfostenquerschnitt (nur Darstellung)
    // T-Eisen liegen hinter den Feldern; Riegel und Latten duerfen daher an
    // Feldgrenzen ohne optische Fuge direkt aneinanderlaufen.
    var feldEinzug = mitPfosten ? (istTeisen ? 0 : q) : 2;
    var teisenRandEinzug = istTeisen ? 6 : 0;

    function terrain(p0, p1, laenge, s) {
      return p0.z + (p1.z - p0.z) * (laenge ? s / laenge : 0);
    }

    for (var i = 0; i < punkte.length - 1; i++) {
      var p0 = punkte[i], p1 = punkte[i + 1];
      var vx = p1.x - p0.x, vy = p1.y - p0.y;
      var laenge = Math.hypot(vx, vy);
      if (laenge < 1) continue;
      var ex = vx / laenge, ey = vy / laenge;
      var yaw = Math.atan2(vy, vx);
      var frontX = ey, frontY = -ex;
      var backX = -frontX, backY = -frontY;

      function fuegeRiegelHinzu(cx, cy, zUnten, laengeRiegel, neigung) {
        var riegelTiefe = 4.5;
        var versatz = lattenTiefe / 2 + riegelTiefe / 2 - 0.5;
        var nachVorne = skizze.riegelVorne === true;
        var ox = nachVorne ? frontX : backX;
        var oy = nachVorne ? frontY : backY;
        cx += ox * versatz;
        cy += oy * versatz;
        if (skizze.riegel === 'rund') {
          formen.push(riegelForm(cx, cy, zUnten + 4, yaw, neigung || 0, laengeRiegel, holz.riegel));
        } else {
          boxen.push({ x: cx, y: cy, z: zUnten, dx: laengeRiegel, dy: riegelTiefe, dz: 8,
            yaw: yaw, pitch: neigung || 0, farbe: holz.riegel });
        }
      }

      function fuegeLatteHinzu(cx, cy, zUnten, breite, index) {
        var versatz = Number(skizze.hoehenversatz) || 0;
        var lattenHoehe = Math.max(10, hoehe - (index % 2 ? versatz : 0));
        if (skizze.tiefenwechsel && index % 2) {
          cx += backX * (lattenTiefe + 1.2);
          cy += backY * (lattenTiefe + 1.2);
        }
        formen.push(lattenForm(cx, cy, zUnten, yaw, breite, lattenHoehe, latte, skizze, holz.latte));
      }

      // Sichtbare Haltetechnik am Pfostenfuß: IPF = Rückseite des T-Eisens
      // (Streifen in der Nut), H-Steg = seitliche Laschen, Dübel-Varianten
      // zusätzlich mit Bodenplatte.
      function halterungAmPfosten(px, py, zGrund) {
        var art = plan.halterung;
        if (!art) return;
        var ox = frontX, oy = frontY;          // senkrecht zum Zaunverlauf (Vorderseite: Start-Kamera schaut von -y)
        var o = q / 2 + 0.8;
        if (art === 'ipf' || art === 'ipf-duebel') {
          boxen.push({ x: px + ox * o, y: py + oy * o, z: zGrund - 2, dx: 6, dy: 1.6, dz: 52, yaw: yaw, farbe: FARBEN.stahl, prio: 1 });
        }
        if (art === 'hsteg' || art === 'duebelhalter') {
          var dzLasche = art === 'hsteg' ? 34 : 26;
          boxen.push({ x: px + ox * o, y: py + oy * o, z: zGrund - 2, dx: 7, dy: 1.6, dz: dzLasche, yaw: yaw, farbe: FARBEN.stahl, prio: 1 });
          boxen.push({ x: px - ox * o, y: py - oy * o, z: zGrund - 2, dx: 7, dy: 1.6, dz: dzLasche, yaw: yaw, farbe: FARBEN.stahl, prio: 1 });
        }
        if (art === 'ipf-duebel' || art === 'duebelhalter') {
          boxen.push({ x: px, y: py, z: zGrund - 1.5, dx: q + 8, dy: q + 8, dz: 2, yaw: yaw, farbe: FARBEN.stahl, prio: 1 });
        }
      }

      // Pfosten-Helfer fuer Holzpfosten und rueckseitige T-Eisen.
      function verschoben(px, py, abstand) {
        return { x: px + backX * abstand, y: py + backY * abstand };
      }

      function teisenPfosten(px, py, zGrund, zUnten, zOben, riegelBasis) {
        var unten = plan.befestigungId === 'einbetonieren' ? zGrund - 20 : zGrund - 1.5;
        var basis = riegelBasis || zGrund;
        var riegelHoehen = [0.22, 0.68].map(function (fr) { return basis + hoehe * fr; });
        var obereRiegelKante = riegelHoehen[1] + 8;
        var oben = obereRiegelKante + 5;
        var dz = Math.max(12, oben - unten);
        var profil = verschoben(px, py, 6.0);
        var steeg = verschoben(px, py, 7.4);

        // 40/40/5-mm-T-Profil: zwei schlanke Stege statt eines Holzpfosten-Quaders.
        boxen.push({ x: profil.x, y: profil.y, z: unten, dx: 4, dy: 0.5, dz: dz, yaw: yaw, farbe: FARBEN.stahl, prio: -1 });
        boxen.push({ x: steeg.x, y: steeg.y, z: unten, dx: 0.5, dy: 3.8, dz: dz, yaw: yaw, farbe: FARBEN.stahlDunkel, prio: -1 });

        if (plan.befestigungId === 'aufduebeln') {
          var platte = verschoben(px, py, 6.4);
          boxen.push({ x: platte.x, y: platte.y, z: zGrund - 1.5, dx: 14, dy: 9, dz: 1.5, yaw: yaw, farbe: FARBEN.stahlDunkel, prio: -1 });
        }

        riegelHoehen.forEach(function (zRiegel) {
          var lasche = verschoben(px, py, 3.8);
          var schraubeLinks = verschoben(px - Math.cos(yaw) * 4.5, py - Math.sin(yaw) * 4.5, 1.6);
          var schraubeRechts = verschoben(px + Math.cos(yaw) * 4.5, py + Math.sin(yaw) * 4.5, 1.6);
          boxen.push({ x: lasche.x, y: lasche.y, z: zRiegel + 2, dx: 22, dy: 4.8, dz: 4, yaw: yaw, farbe: FARBEN.stahlDunkel, prio: -1 });
          boxen.push({ x: schraubeLinks.x, y: schraubeLinks.y, z: zRiegel + 4, dx: 1.8, dy: 1.2, dz: 1.8, yaw: yaw, farbe: '#5f6568', prio: 0 });
          boxen.push({ x: schraubeRechts.x, y: schraubeRechts.y, z: zRiegel + 4, dx: 1.8, dy: 1.2, dz: 1.8, yaw: yaw, farbe: '#5f6568', prio: 0 });
        });
      }

      function pfostenAmPunkt(px, py, zGrund, zUnten, zOben, riegelBasis) {
        if (istTeisen) {
          teisenPfosten(px, py, zGrund, zUnten, zOben, riegelBasis);
          return;
        }
        boxen.push({
          x: px, y: py, z: zUnten,
          dx: q, dy: q, dz: zOben - zUnten,
          yaw: yaw, farbe: pfostenFarbe
        });
        halterungAmPfosten(px, py, zGrund);
      }

      // Bodenstreifen unter dem Abschnitt.
      var bb = 170;
      boeden.push({
        farbe: i % 2 ? FARBEN.bodenDunkel : FARBEN.boden,
        punkte: [
          [p0.x - ey * bb, p0.y + ex * bb, p0.z - 2],
          [p1.x - ey * bb, p1.y + ex * bb, p1.z - 2],
          [p1.x + ey * bb, p1.y - ex * bb, p1.z - 2],
          [p0.x + ey * bb, p0.y - ex * bb, p0.z - 2]
        ]
      });

      // Feldlängen für diesen Abschnitt; Überstand kürzt das letzte Feld (wie vor Ort)
      var felder = (plan.felderProSegment && plan.felderProSegment[i] || []).slice();
      if (!felder.length) felder = [laenge];
      var summe = felder.reduce(function (a, b) { return a + b; }, 0);
      if (summe > laenge && felder.length) {
        felder[felder.length - 1] = Math.max(40, felder[felder.length - 1] - (summe - laenge));
      }

      var s = 0;
      var vorigeOberkante = null;
      for (var fIdx = 0; fIdx < felder.length; fIdx++) {
        var flen = Math.min(felder[fIdx], laenge - s);
        if (flen <= 4) break;
        var zA = terrain(p0, p1, laenge, s);
        var zB = terrain(p0, p1, laenge, s + flen);
        var mx = p0.x + ex * (s + flen / 2);
        var my = p0.y + ey * (s + flen / 2);

        if (plan.schraeg) {
          // Schrägmontage (Bausatz): Riegel folgen dem Hang, Latten senkrecht.
          var neigung = Math.atan2(zB - zA, flen);
          if (mitPfosten) {
            var sPfostenS = s + Math.min(teisenRandEinzug, flen / 4);
            var zPfostenS = terrain(p0, p1, laenge, sPfostenS);
            pfostenAmPunkt(p0.x + ex * sPfostenS, p0.y + ey * sPfostenS, zPfostenS, zPfostenS - 3, zPfostenS + hoehe + 7, zA + 1);
          }
          [0.22, 0.68].forEach(function (fr) {
            fuegeRiegelHinzu(mx, my, (zA + zB) / 2 + 1 + hoehe * fr,
              (flen - feldEinzug) / Math.cos(neigung), neigung);
          });
          // Latten nur über die lichte Weite verteilen (nicht in die Pfosten hinein)
          var innenS = feldEinzug;
          var anzahlS = Math.max(2, Math.ceil(proM * flen / 100));
          var pitchS = (flen - innenS) / anzahlS;
          var lbEffS = Math.min(lb, pitchS * 0.75);
          for (var lsi = 0; lsi < anzahlS; lsi++) {
            var lsS = s + innenS / 2 + (lsi + 0.5) * pitchS;
            fuegeLatteHinzu(p0.x + ex * lsS, p0.y + ey * lsS,
              terrain(p0, p1, laenge, lsS) + 1, lbEffS, lsi);
          }
          s += flen;
          continue;
        }

        var zBase = Math.max(zA, zB) + 1;  // Feld waagerecht, am Hang gestuft

        // Pfosten am Feldanfang
        if (mitPfosten) {
          var sPfosten = s + Math.min(teisenRandEinzug, flen / 4);
          var zPfosten = terrain(p0, p1, laenge, sPfosten);
          var untenPfosten = Math.min(zPfosten, zBase) - 3;
          var obenPfosten = Math.max(zBase, vorigeOberkante || zBase) + hoehe + 7;
          pfostenAmPunkt(p0.x + ex * sPfosten, p0.y + ey * sPfosten, zPfosten, untenPfosten, obenPfosten, zBase);
        }

        // Zwei Querriegel
        [0.22, 0.68].forEach(function (fr) {
          fuegeRiegelHinzu(mx, my, zBase + hoehe * fr, flen - feldEinzug, 0);
        });

        // Latten – nur über die lichte Weite verteilen (nicht in die Pfosten hinein)
        var innen = feldEinzug;
        var anzahl = Math.max(2, Math.ceil(proM * flen / 100));
        var pitch = (flen - innen) / anzahl;
        var lbEff = Math.min(lb, pitch * 0.75);
        for (var li = 0; li < anzahl; li++) {
          var ls = s + innen / 2 + (li + 0.5) * pitch;
          fuegeLatteHinzu(p0.x + ex * ls, p0.y + ey * ls, zBase, lbEff, li);
        }

        vorigeOberkante = zBase;
        s += flen;
      }

      // Abschluss-Pfosten am Ende des Abschnitts (der nächste Abschnitt
      // beginnt mit seinem eigenen Anfangspfosten am selben Punkt – beim
      // letzten Abschnitt braucht es den Endpfosten explizit)
      if (mitPfosten && i === punkte.length - 2) {
        var sEndPfosten = Math.max(0, laenge - teisenRandEinzug);
        var zEndPfosten = terrain(p0, p1, laenge, sEndPfosten);
        pfostenAmPunkt(
          p0.x + ex * sEndPfosten, p0.y + ey * sEndPfosten, zEndPfosten, zEndPfosten - 3,
          (vorigeOberkante || p1.z) + hoehe + 7,
          vorigeOberkante || p1.z
        );
      }
    }

    return { boxen: boxen, formen: formen, boeden: boeden };
  }

  return { erstelle: erstelle, szeneAusPlan: szeneAusPlan };
}));
